import React, { useState } from 'react';
import StudentSharingPanel from './StudentSharingPanel';
import ParentPortal from './ParentPortal';
import ShareButton from './ShareButton';
import { User, Users } from 'lucide-react';

/**
 * Demo component to showcase the sharing functionality
 * This demonstrates how the sharing system works from both
 * student and parent perspectives
 */
const SharingDemo = () => {
  const [view, setView] = useState('student'); // 'student' or 'parent'

  return (
    <div className="min-h-screen bg-stone-50">
      {/* View Switcher */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Parent-Student Sharing System Demo</h1>
            <div className="flex items-center bg-stone-100 rounded-lg p-1">
              <button
                onClick={() => setView('student')}
                className={`px-4 py-2 rounded-md flex items-center gap-2 transition-all ${
                  view === 'student'
                    ? 'bg-white text-azure-600 shadow-sm'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                <User size={18} />
                Student View
              </button>
              <button
                onClick={() => setView('parent')}
                className={`px-4 py-2 rounded-md flex items-center gap-2 transition-all ${
                  view === 'parent'
                    ? 'bg-white text-azure-600 shadow-sm'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
              >
                <Users size={18} />
                Parent View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Info Banner */}
      {view === 'student' && (
        <div className="bg-azure-50 border-b border-azure-200">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <p className="text-sm text-azure-800">
              <strong>Student View:</strong> This shows how students can manage what they share with parents.
              Students have full control over individual items and can set sharing preferences for different categories.
            </p>
          </div>
        </div>
      )}

      {view === 'parent' && (
        <div className="bg-emerald-50 border-b border-emerald-200">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <p className="text-sm text-emerald-800">
              <strong>Parent View:</strong> This shows what parents see in their portal.
              Parents can view shared items, track timeline progress, see college lists, and provide supportive feedback.
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {view === 'student' ? <StudentSharingPanel /> : <ParentPortal />}

      {/* Example Integration Section */}
      {view === 'student' && (
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-white rounded-xl border p-6">
            <h3 className="text-lg font-semibold mb-4">
              Example: How Share Buttons Integrate with Existing Components
            </h3>
            <p className="text-sm text-stone-600 mb-6">
              These share buttons can be added to any existing component (Essays, Brag Sheet, Recommendations, etc.)
            </p>

            <div className="space-y-4">
              {/* Example Essay Card with Share Button */}
              <div className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">Common App Personal Statement</h4>
                    <p className="text-sm text-stone-600">650 words • Final Draft</p>
                  </div>
                  <ShareButton
                    itemType="essay"
                    itemId="essay_1"
                    itemTitle="Common App Personal Statement"
                    currentStatus="final"
                    variant="default"
                  />
                </div>
              </div>

              {/* Example with Compact Button */}
              <div className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">Why Northwestern Essay</h4>
                    <p className="text-sm text-stone-600">450 words • Draft</p>
                  </div>
                  <ShareButton
                    itemType="essay"
                    itemId="essay_2"
                    itemTitle="Why Northwestern Essay"
                    currentStatus="draft"
                    variant="compact"
                  />
                </div>
              </div>

              {/* Example with Icon-Only Button */}
              <div className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">Activities Brag Sheet</h4>
                    <p className="text-sm text-stone-600">Completed</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="p-2 bg-stone-100 rounded-lg hover:bg-stone-200">
                      Edit
                    </button>
                    <ShareButton
                      itemType="brag_sheet"
                      itemId="brag_1"
                      itemTitle="Activities Brag Sheet"
                      currentStatus="complete"
                      variant="icon-only"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharingDemo;